import React, { useState, useEffect } from 'react';
import { consumptionAPI, storeAPI, itemAPI, unitAPI, clientAPI, getBillUrl, viewBill, downloadBill } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { DataTable } from '../components/DataTable';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Utensils, 
  Loader2, 
  Plus, 
  Eye,
  Pencil,
  Trash2,
  Search,
  Upload,
  Image as ImageIcon,
  X
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { SearchableItemSelect } from '../components/SearchableItemSelect';

const CONSUMPTION_PURPOSES = [
  "Daily Operations",
  "Event Catering",
  "Guest Services",
  "Kitchen Prep",
  "Housekeeping",
  "Maintenance",
  "Staff Meals",
  "Wastage/Spoilage",
  "Other"
];

const ConsumptionPage = () => {
  const [consumptions, setConsumptions] = useState([]);
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [units, setUnits] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStore, setFilterStore] = useState('all');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    store_id: '',
    consumption_date: '',
    purpose: '',
    items: [],
    proof_images: [],
    notes: '',
    client_id: ''
  });
  
  // Current item being added
  const [currentItem, setCurrentItem] = useState({
    item_id: '',
    quantity: '',
    unit_id: '',
    notes: ''
  });

  // Check if user is Super Admin
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  useEffect(() => {
    fetchData();
    
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        setIsSuperAdmin(user?.role_name === 'Super Admin');
      }
    } catch (e) {
      console.error('Error parsing user:', e);
    }
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [consumptionsRes, storesRes, itemsRes, unitsRes, clientsRes] = await Promise.all([
        consumptionAPI.getAll(),
        storeAPI.getAll({ status: 'active' }),
        itemAPI.getAll({ status: 'active' }),
        unitAPI.getAll({ status: 'active' }),
        clientAPI.getAll({ status: 'active' })
      ]);
      setConsumptions(consumptionsRes.data);
      setStores(storesRes.data);
      setItems(itemsRes.data);
      setUnits(unitsRes.data);
      setClients(clientsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (record = null) => {
    if (record) {
      setSelectedRecord(record);
      setFormData({
        store_id: record.store_id || '',
        consumption_date: record.consumption_date?.split('T')[0] || '',
        purpose: record.purpose || '',
        items: record.items || [],
        proof_images: record.proof_images || [],
        notes: record.notes || '',
        client_id: record.client_id || ''
      });
    } else {
      setSelectedRecord(null);
      setFormData({
        store_id: stores[0]?.id || '',
        consumption_date: new Date().toISOString().split('T')[0],
        purpose: '',
        items: [],
        proof_images: [],
        notes: '',
        client_id: ''
      });
    }
    setCurrentItem({ item_id: '', quantity: '', unit_id: '', notes: '' });
    setDialogOpen(true);
  };

  const handleViewRecord = (record) => {
    setSelectedRecord(record);
    setViewDialogOpen(true);
  };

  const handleAddItem = () => {
    if (!currentItem.item_id || !currentItem.quantity) {
      toast.error('Please select an item and enter quantity');
      return;
    }
    
    const item = items.find(i => i.id === currentItem.item_id);
    const unit = units.find(u => u.id === currentItem.unit_id);
    
    setFormData({
      ...formData,
      items: [...formData.items, {
        item_id: currentItem.item_id,
        item_name: item?.name || '',
        quantity: parseFloat(currentItem.quantity),
        unit_id: currentItem.unit_id,
        unit_name: unit?.name || '',
        notes: currentItem.notes
      }]
    });
    
    setCurrentItem({ item_id: '', quantity: '', unit_id: '', notes: '' });
  };

  const handleRemoveItem = (index) => {
    setFormData({
      ...formData,
      items: formData.items.filter((_, i) => i !== index)
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Upload each file to the server
    const uploadedUrls = [];
    for (const file of files) {
      try {
        const response = await consumptionAPI.uploadProof(file);
        uploadedUrls.push(response.data.url);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
        console.error('Upload error:', error);
      }
    }
    
    if (uploadedUrls.length > 0) {
      setFormData({
        ...formData,
        proof_images: [...formData.proof_images, ...uploadedUrls]
      });
      toast.success(`${uploadedUrls.length} image(s) uploaded`);
    }
  };

  const handleRemoveImage = (index) => {
    setFormData({
      ...formData,
      proof_images: formData.proof_images.filter((_, i) => i !== index)
    });
  };

  const handleSave = async () => {
    if (!formData.store_id) {
      toast.error('Please select a store');
      return;
    }
    if (!formData.purpose) {
      toast.error('Please select a purpose');
      return;
    }
    if (formData.items.length === 0) {
      toast.error('Please add at least one item');
      return;
    }

    try {
      setSaving(true);
      
      if (selectedRecord) {
        await consumptionAPI.update(selectedRecord.id, formData);
        toast.success('Consumption record updated');
      } else {
        await consumptionAPI.create(formData);
        toast.success('Consumption recorded successfully');
      }
      
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save consumption');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    if (!window.confirm(`Are you sure you want to delete ${record.consumption_number}? This will restore the stock.`)) {
      return;
    }
    
    try {
      await consumptionAPI.delete(record.id);
      toast.success('Consumption deleted and stock restored');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  // Filter consumptions
  const filteredConsumptions = consumptions.filter(c => {
    const matchesStore = filterStore === 'all' || c.store_id === filterStore;
    const matchesSearch = !searchQuery || 
      c.consumption_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.purpose?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStore && matchesSearch;
  });

  return (
    <div className="space-y-6" data-testid="consumption-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
            <Utensils className="h-6 w-6 md:h-7 md:w-7" />
            Consumption Log
          </h1>
          <p className="text-muted-foreground">Record and track stock consumption with proof</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="record-consumption-btn">
          <Plus className="h-4 w-4 mr-2" />
          Record Consumption
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by number or purpose..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterStore} onValueChange={setFilterStore}>
              <SelectTrigger className="w-full md:w-[200px]">
                <SelectValue placeholder="All Stores" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stores</SelectItem>
                {stores.map(store => (
                  <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Consumption List */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Consumption Records ({filteredConsumptions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            module="consumption"
            data={filteredConsumptions}
            loading={loading}
            title="Consumption Report"
            columns={[
              {
                key: 'consumption_number',
                label: 'Record #',
                sortable: true,
                render: (value) => <span className="font-mono font-medium">{value}</span>
              },
              {
                key: 'consumption_date',
                label: 'Date',
                sortable: true,
                render: (value) => formatDate(value)
              },
              {
                key: 'store_name',
                label: 'Store',
                sortable: true
              },
              {
                key: 'purpose',
                label: 'Purpose',
                sortable: true
              },
              {
                key: 'total_items',
                label: 'Items',
                render: (value) => <Badge variant="secondary">{value}</Badge>
              },
              {
                key: 'client_name',
                label: 'Client/Event',
                render: (value) => value || '-'
              },
              {
                key: 'proof_images',
                label: 'Proof',
                render: (value) => value?.length > 0 ? (
                  <Badge variant="outline" className="gap-1">
                    <ImageIcon className="h-3 w-3" />
                    {value.length}
                  </Badge>
                ) : '-'
              },
              {
                key: 'recorded_by_name',
                label: 'Recorded By',
                render: (value) => value || '-'
              }
            ]}
            renderActions={(record) => (
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleViewRecord(record)}
                  title="View Details"
                >
                  <Eye className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleOpenDialog(record)}
                  title="Edit"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                {isSuperAdmin && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(record)}
                    title="Delete"
                    className="text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            )}
          />
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              {selectedRecord ? 'Edit Consumption Record' : 'Record Consumption'}
            </DialogTitle>
            <DialogDescription>
              Record items consumed from your store inventory with proof documentation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Store *</Label>
                <Select
                  value={formData.store_id}
                  onValueChange={(value) => setFormData({ ...formData, store_id: value })}
                  disabled={!!selectedRecord}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select store" />
                  </SelectTrigger>
                  <SelectContent>
                    {stores.map(store => (
                      <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={formData.consumption_date}
                  onChange={(e) => setFormData({ ...formData, consumption_date: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Purpose *</Label>
                <Select
                  value={formData.purpose}
                  onValueChange={(value) => setFormData({ ...formData, purpose: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONSUMPTION_PURPOSES.map(purpose => (
                      <SelectItem key={purpose} value={purpose}>{purpose}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Client/Event Link (Optional) */}
            <div className="space-y-2">
              <Label>Link to Client/Event (Optional)</Label>
              <Select
                value={formData.client_id || "none"}
                onValueChange={(value) => setFormData({ ...formData, client_id: value === "none" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select client/event" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Client</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Items Section */}
            <div className="space-y-4">
              <h4 className="font-medium">Items Consumed</h4>
              
              {/* Add Item Form */}
              <div className="grid grid-cols-12 gap-2 p-3 bg-muted rounded-lg">
                <div className="col-span-12 md:col-span-4">
                  <SearchableItemSelect
                    items={items}
                    value={currentItem.item_id}
                    onSelect={(value) => {
                      const item = items.find(i => i.id === value);
                      setCurrentItem({
                        ...currentItem,
                        item_id: value,
                        unit_id: item?.default_unit_id || ''
                      });
                    }}
                    placeholder="Select item..."
                  />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Input
                    type="number"
                    placeholder="Qty"
                    min="0.01"
                    step="0.01"
                    value={currentItem.quantity}
                    onChange={(e) => setCurrentItem({ ...currentItem, quantity: e.target.value })}
                  />
                </div>
                <div className="col-span-4 md:col-span-2">
                  <Select
                    value={currentItem.unit_id}
                    onValueChange={(value) => setCurrentItem({ ...currentItem, unit_id: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Unit" />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map(unit => (
                        <SelectItem key={unit.id} value={unit.id}>
                          {unit.name} {unit.symbol && `(${unit.symbol})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-3 md:col-span-3">
                  <Input
                    placeholder="Notes"
                    value={currentItem.notes}
                    onChange={(e) => setCurrentItem({ ...currentItem, notes: e.target.value })}
                  />
                </div>
                <div className="col-span-1">
                  <Button onClick={handleAddItem} size="icon" className="w-full">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Items List */}
              {formData.items.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead className="text-right">Quantity</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-right">{item.quantity}</TableCell>
                        <TableCell>{item.unit_name || '-'}</TableCell>
                        <TableCell className="text-muted-foreground">{item.notes || '-'}</TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleRemoveItem(index)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            {/* Proof Images */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <ImageIcon className="h-4 w-4" />
                Proof Images
              </h4>
              
              <div className="flex flex-wrap gap-4">
                {formData.proof_images.map((img, index) => (
                  <div key={index} className="relative group">
                    <img 
                      src={getBillUrl(img)} 
                      alt={`Proof ${index + 1}`} 
                      className="w-24 h-24 object-cover rounded-lg border"
                    />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute -top-2 -right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => handleRemoveImage(index)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                ))}
                
                <label className="w-24 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary transition-colors">
                  <Upload className="h-6 w-6 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground mt-1">Upload</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={handleImageUpload}
                  />
                </label>
              </div>
              <p className="text-xs text-muted-foreground">
                Upload photos of consumption log, receipts, or any supporting documents
              </p>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional information..."
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {selectedRecord ? 'Update' : 'Record'} Consumption
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Consumption Record - {selectedRecord?.consumption_number}</DialogTitle>
          </DialogHeader>

          {selectedRecord && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4 bg-muted rounded-lg">
                <div>
                  <span className="text-muted-foreground text-sm">Date</span>
                  <p className="font-medium">{formatDate(selectedRecord.consumption_date)}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Store</span>
                  <p className="font-medium">{selectedRecord.store_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Purpose</span>
                  <p className="font-medium">{selectedRecord.purpose}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-sm">Recorded By</span>
                  <p className="font-medium">{selectedRecord.recorded_by_name}</p>
                </div>
              </div>

              {selectedRecord.client_name && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <span className="text-blue-600 text-sm">Linked to Client/Event:</span>
                  <p className="font-medium text-blue-700">{selectedRecord.client_name}</p>
                </div>
              )}

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Item</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Unit</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {selectedRecord.items?.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell className="font-medium">{item.item_name}</TableCell>
                      <TableCell className="text-right">{item.quantity}</TableCell>
                      <TableCell>{item.unit_name || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{item.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {selectedRecord.proof_images?.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium">Proof Images</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedRecord.proof_images.map((img, index) => (
                      <div key={index} className="relative group">
                        <img 
                          src={getBillUrl(img)} 
                          alt={`Proof ${index + 1}`} 
                          className="w-20 h-20 object-cover rounded-lg border hover:border-primary transition-colors cursor-pointer"
                          onClick={() => viewBill(img)}
                        />
                        <div className="absolute -bottom-1 -right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                          <Button
                            size="icon"
                            variant="secondary"
                            className="h-5 w-5 rounded-full"
                            onClick={() => viewBill(img)}
                            title="View"
                          >
                            <Eye className="h-3 w-3" />
                          </Button>
                          <Button
                            size="icon"
                            variant="secondary"
                            className="h-5 w-5 rounded-full"
                            onClick={() => downloadBill(img, `proof_${selectedRecord.consumption_number}_${index + 1}`)}
                            title="Download"
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {selectedRecord.notes && (
                <div className="p-3 bg-muted rounded-lg">
                  <span className="text-muted-foreground text-sm">Notes</span>
                  <p>{selectedRecord.notes}</p>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ConsumptionPage;
